(() => {
  const A = 3.9083e-3, B = -5.775e-7, C = -4.183e-12;
  const resistance = (t, r0) => r0 * (1 + A * t + B * t * t + (t < 0 ? C * (t - 100) * t * t * t : 0));
  const toCelsius = (n, unit) => unit === 'f' ? (n - 32) * 5 / 9 : n;
  const fromCelsius = (n, unit) => unit === 'f' ? n * 9 / 5 + 32 : n;
  const rounded = (n, digits = 4) => (n + Math.sign(n) * Number.EPSILON * Math.abs(n)).toFixed(digits);
  const format = (n, digits = 4) => (Number(rounded(n, digits)) || 0).toLocaleString('en-US', { maximumFractionDigits: digits });
  const inputNumber = (n) => String(Number(n.toPrecision(12)));
  const temperature = (r, r0) => {
    if (r === r0) return 0;
    if (r <= resistance(-200, r0)) return -200;
    if (r >= resistance(850, r0)) return 850;
    let low = -200, high = 850;
    for (let i = 0; i < 60; i++) {
      const mid = (low + high) / 2;
      if (resistance(mid, r0) < r) low = mid;
      else high = mid;
    }
    return (low + high) / 2;
  };
  function initialize(root) {
    if (root.dataset.rtdReady) return;
    const get = (key) => root.querySelector(`[data-rtd-${key}]`);
    const sensor = get('sensor'), direction = get('direction'), unit = get('unit'), source = get('source');
    const read = () => source.value.trim() === '' ? NaN : Number(source.value);
    let state = null, message = '', previousUnit = unit.value;
    function refresh() {
      const r0 = Number(sensor.value), forward = direction.value === 'temperature';
      const symbol = unit.value === 'f' ? '°F' : '°C';
      const value = read(), minR = resistance(-200, r0), maxR = resistance(850, r0);
      const minT = fromCelsius(-200, unit.value), maxT = fromCelsius(850, unit.value);
      get('source-label').textContent = forward ? `Temperature (${symbol})` : 'Sensor resistance (Ω)';
      source.min = String(forward ? minT : minR);
      source.max = String(forward ? maxT : maxR);
      get('source-hint').textContent = forward ? `Curve range: ${minT} to ${maxT} ${symbol}. Your probe may have a narrower rating.` : `Curve range: ${format(minR, 6)} to ${format(maxR, 6)} Ω. Enter element resistance, excluding lead resistance.`;
      get('result-label').textContent = forward ? `Calculated Pt${r0} resistance` : 'Calculated temperature';
      get('result-unit').textContent = forward ? 'Ω' : symbol;
      get('curve-note').textContent = `Pt${r0} · IEC 60751 nominal curve · α ≈ 0.00385`;
      let error = '';
      const t = forward ? toCelsius(value, unit.value) : temperature(value, r0);
      if (!Number.isFinite(value)) error = `Enter a valid ${forward ? 'temperature' : 'resistance'} value.`;
      else if (forward && (t < -200 || t > 850)) error = `Enter a temperature from ${minT} to ${maxT} ${symbol}.`;
      // Tolerance here only absorbs floating-point endpoint representation.
      else if (!forward && (value < minR - 1e-9 || value > maxR + 1e-9)) error = `Resistance is outside the Pt${r0} curve range. Check the sensor type, reading and wiring.`;
      state = error ? null : { t, r: forward ? resistance(t, r0) : value };
      source.setAttribute('aria-invalid', String(!!error));
      get('error').textContent = error;
      get('error').hidden = !error;
      get('copy').disabled = !!error;
      get('copy-status').textContent = '';
      get('value').textContent = state ? format(forward ? state.r : fromCelsius(state.t, unit.value), forward ? 4 : 3) : '—';
      get('formula').textContent = state && state.t < 0 ? 'R = R₀ [1 + At + Bt² + C(t − 100)t³]' : 'R = R₀ (1 + At + Bt²)';
      get('substitution').textContent = state ? `At ${format(state.t, 4)} °C: Pt${r0} ≈ ${format(state.r, 4)} Ω.${forward ? '' : ' Temperature found by solving the curve.'}` : 'Complete the input to see the corresponding point on the curve.';
      message = [
        'Pt100 / Pt1000 RTD inquiry',
        `Sensor: Pt${r0} (${r0} ohms at 0 °C)`,
        'Curve: IEC 60751 nominal, alpha approximately 0.00385',
        `Direction: ${forward ? 'Temperature to resistance' : 'Resistance to temperature'}`,
        `Input: ${source.value.trim() || '(not entered)'} ${forward ? symbol : 'Ω'}`,
        `Result: ${state ? get('value').textContent + ' ' + (forward ? 'Ω' : symbol) + ' (approx.)' : 'Unavailable — ' + error}`,
        ...(state ? [`Corresponding temperature: ${format(state.t, 4)} °C`, `Sensor resistance: ${format(state.r, 4)} Ω`] : []),
        'Curve temperature range: -200 to 850 °C; actual probe rating must be checked.',
        'Lead resistance, calibration and probe tolerance are not included.',
        'Please help me check sensor or temperature input module compatibility.',
        `Page: ${root.dataset.pageUrl}`
      ].join('\n');
      get('email').setAttribute('href', 'mailto:' + root.dataset.email + '?subject=' + encodeURIComponent('Pt100 / Pt1000 RTD inquiry') + '&body=' + encodeURIComponent(message));
      get('whatsapp').setAttribute('href', 'https://wa.me/8613533898924?text=' + encodeURIComponent(message));
      get('contact-note').textContent = state ? 'Your sensor type, input and result are included. Review before sending.' : 'You can still ask for help. The message marks this calculation as invalid or incomplete.';
    }
    root.addEventListener('input', (event) => { if (event.target === source) refresh(); });
    root.addEventListener('change', (event) => {
      if (!event.target.matches('select')) return;
      if (event.target === direction && state) source.value = inputNumber(direction.value === 'resistance' ? state.r : fromCelsius(state.t, unit.value));
      if (event.target === unit) {
        if (direction.value === 'temperature' && Number.isFinite(read())) source.value = inputNumber(fromCelsius(toCelsius(read(), previousUnit), unit.value));
        previousUnit = unit.value;
      }
      refresh();
    });
    root.querySelectorAll('[data-rtd-example]').forEach((button) => {
      button.addEventListener('click', () => {
        direction.value = 'temperature';
        source.value = String(fromCelsius(Number(button.dataset.rtdExample), unit.value));
        refresh();
      });
    });
    get('copy').addEventListener('click', async () => {
      if (!state) return;
      try { await navigator.clipboard.writeText(message); get('copy-status').textContent = 'Calculation copied.'; }
      catch { get('copy-status').textContent = 'Copy unavailable. Select the result or use an inquiry button.'; }
    });
    get('download').disabled = false;
    get('download').addEventListener('click', () => {
      try {
        const rows = [
          ['KOEED Pt100 / Pt1000 nominal resistance table'],
          ['Curve', 'IEC 60751 alpha approximately 0.00385'],
          ['A', A, 'B', B, 'C', C],
          ['Range', '-200 to 850 deg C', 'Step', '10 deg C'],
          ['Note', 'Ideal element resistance; excludes lead resistance and sensor tolerance.'],
          ['Source', 'https://www.ti.com/lit/pdf/sbaa275'],
          ['Temperature_C', 'Temperature_F', 'Pt100_ohms', 'Pt1000_ohms']
        ];
        for (let t = -200; t <= 850; t += 10) rows.push([t, fromCelsius(t, 'f'), rounded(resistance(t, 100)), rounded(resistance(t, 1000))]);
        const csv = '\uFEFF' + rows.map(row => row.map(cell => '"' + String(cell).replaceAll('"', '""') + '"').join(',')).join('\r\n');
        const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
        const link = document.createElement('a');
        link.href = url; link.download = 'koeed-pt100-pt1000-iec60751-table.csv';
        document.body.appendChild(link); link.click(); link.remove();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
        get('download-status').textContent = 'CSV prepared: 106 temperature rows, both sensor types.';
      } catch { get('download-status').textContent = 'Download unavailable. Use the reference table below.'; }
    });
    refresh();
    root.dataset.rtdReady = 'true';
  }
  const init = () => document.querySelectorAll('[data-koeed-rtd]').forEach(initialize);
  init();
  document.addEventListener('shopify:section:load', init);
})();
